import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Flame, Trophy } from "lucide-react";
import { LEVEL_COLORS, BACKEND_URL } from "../lib/api";
import BrandWordmark from "../components/BrandWordmark";

export default function SharedProgress() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/share/progress/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Link not found");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6">
        <div className="text-center">
          <div className="font-display text-3xl uppercase mb-2">Link unavailable</div>
          <div className="text-neutral-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center font-display text-xl">Loading…</div>;

  const lvlColor = LEVEL_COLORS[data.level.name];
  const chartData = (data.body_weights || []).map((w) => ({ date: w.date.slice(5), weight: w.weight_kg }));

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <header className="border-b border-neutral-900">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-blue-500" />
            <BrandWordmark size="sm" />
          </div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Read-only progress share</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">PROGRESS REPORT</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1">{data.name}</h1>
        {data.gym_name && <div className="text-sm text-neutral-400 mt-1">@ {data.gym_name}</div>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5 border border-neutral-800">
          <Stat label="Level" value={data.level.name} color={lvlColor} />
          <Stat label="Total Lifted" value={`${Math.round(data.total_weight_kg)} kg`} />
          <Stat label="Sessions" value={data.total_sessions} />
          <Stat label="PRs" value={data.total_prs} />
        </div>

        {chartData.length > 0 && (
          <section className="mt-7">
            <h2 className="font-display text-2xl uppercase mb-3">Body Weight Trend</h2>
            <div className="border border-neutral-800 p-3 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#262626" />
                  <XAxis dataKey="date" stroke="#737373" />
                  <YAxis stroke="#737373" />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #262626", borderRadius: 0 }} />
                  <Line type="monotone" dataKey="weight" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {data.personal_bests?.length > 0 && (
          <section className="mt-7">
            <h2 className="font-display text-2xl uppercase mb-3">Personal Bests</h2>
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {data.personal_bests.map((p) => (
                <div key={p.exercise_id} className="p-3 flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg uppercase">{p.exercise_name}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider">{p.best_weight} kg · {p.best_reps} reps</div>
                  </div>
                  <Trophy className="w-5 h-5 text-blue-500" />
                </div>
              ))}
            </div>
          </section>
        )}

        {data.recent_sessions?.length > 0 && (
          <section className="mt-7 mb-10">
            <h2 className="font-display text-2xl uppercase mb-3">Recent Sessions</h2>
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {data.recent_sessions.map((s) => (
                <div key={s.session_id} className="p-3 flex items-center justify-between text-sm">
                  <div>{s.completed_at?.slice(0, 10)}</div>
                  <div className="text-neutral-400 text-xs uppercase tracking-wider">{s.total_sets} sets · {s.total_reps} reps · {Math.round(s.total_weight_kg)} kg</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-4 border-r border-b border-neutral-800 last:border-r-0">
      <div className="text-xs tracking-widest uppercase text-neutral-500">{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}
